import { useState } from 'react';
import { 
  AlertTriangle, 
  Info, 
  AlertCircle, 
  XCircle,
  Clock,
  Cpu,
  Radio,
  Calendar,
  CheckCircle2,
  X,
  Check,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import type { ApiAlarm, AlarmType } from '@/types/api';
import { formatDistanceToNow } from 'date-fns';
import { es } from 'date-fns/locale';

interface AlarmDetailDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  alarm: ApiAlarm | null;
  onAcknowledge: (id: string) => void;
  onResolve: (id: string, actionTaken?: string) => void;
  isAcknowledging?: boolean;
  isResolving?: boolean;
}

const getAlarmIcon = (type: AlarmType) => {
  const iconMap: Record<string, React.ReactNode> = {
    irrigation_missed: <Calendar className="w-5 h-5" />,
    irrigation_out_of_hours: <Clock className="w-5 h-5" />,
    supervision_started: <CheckCircle2 className="w-5 h-5" />,
    supervision_stopped: <X className="w-5 h-5" />,
    time_sync_failed: <AlertCircle className="w-5 h-5" />,
    time_sync_corrected: <CheckCircle2 className="w-5 h-5" />,
    device_offline: <XCircle className="w-5 h-5" />,
    device_back_online: <CheckCircle2 className="w-5 h-5" />,
    schedule_sync_failed: <AlertCircle className="w-5 h-5" />,
    schedule_conflict: <AlertTriangle className="w-5 h-5" />,
    schedule_mismatch: <AlertTriangle className="w-5 h-5" />,
    schedule_missing_device: <XCircle className="w-5 h-5" />,
    schedule_extra_device: <AlertTriangle className="w-5 h-5" />,
    schedule_verification_err: <AlertCircle className="w-5 h-5" />,
  };
  return iconMap[type] || <Info className="w-5 h-5" />;
};

const getSeverityColors = (severity: string) => {
  const colors = {
    info: 'bg-blue-500/10 text-blue-600 border-blue-500/20',
    warning: 'bg-yellow-500/10 text-yellow-600 border-yellow-500/20',
    alarm: 'bg-orange-500/10 text-orange-600 border-orange-500/20',
    critical: 'bg-red-500/10 text-red-600 border-red-500/20',
  };
  return colors[severity as keyof typeof colors] || colors.info;
};

export function AlarmDetailDialog({
  open,
  onOpenChange,
  alarm,
  onAcknowledge,
  onResolve,
  isAcknowledging = false,
  isResolving = false,
}: AlarmDetailDialogProps) {
  const [actionTaken, setActionTaken] = useState('');

  if (!alarm) return null;

  const isActive = alarm.status === 'active';
  const isAcknowledged = alarm.status === 'acknowledged';
  const isResolved = alarm.status === 'resolved';

  const timeAgo = formatDistanceToNow(new Date(alarm.createdAt), {
    addSuffix: true,
    locale: es,
  });

  const handleResolve = () => {
    onResolve(alarm.id, actionTaken || undefined);
    setActionTaken('');
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-start gap-4">
            <div className={cn('p-3 rounded-lg', getSeverityColors(alarm.severity))}>
              {getAlarmIcon(alarm.type)}
            </div>
            <div className="flex-1">
              <DialogTitle className="text-xl">{alarm.title}</DialogTitle>
              <DialogDescription className="mt-2">
                {alarm.message}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Badges */}
          <div className="flex flex-wrap gap-2">
            <Badge className={cn('text-sm', getSeverityColors(alarm.severity))}>
              {alarm.severityLabel}
            </Badge>
            <Badge variant="outline">
              {alarm.typeLabel}
            </Badge>
            <Badge variant={isActive ? 'destructive' : isAcknowledged ? 'secondary' : 'default'}>
              {isActive ? 'Activa' : isAcknowledged ? 'Reconocida' : 'Resuelta'}
            </Badge>
          </div>

          {/* Información del dispositivo */}
          <div className="grid grid-cols-2 gap-4">
            {alarm.controllerName && (
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Controladora</Label>
                <div className="flex items-center gap-2">
                  <Cpu className="w-4 h-4 text-muted-foreground" />
                  <span className="text-sm font-medium">{alarm.controllerName}</span>
                </div>
              </div>
            )}
            {alarm.sensorName && (
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Sensor</Label>
                <div className="flex items-center gap-2">
                  <Radio className="w-4 h-4 text-muted-foreground" />
                  <span className="text-sm font-medium">{alarm.sensorName}</span>
                </div>
              </div>
            )}
            {alarm.valveName && (
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Válvula</Label>
                <span className="text-sm font-medium">{alarm.valveName}</span>
              </div>
            )}
            {alarm.scheduleName && (
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Programación</Label>
                <span className="text-sm font-medium">{alarm.scheduleName}</span>
              </div>
            )}
          </div>

          {/* Detalles adicionales */}
          {alarm.details && (
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Detalles</Label>
              <div className="p-3 bg-muted rounded-md text-sm font-mono">
                {alarm.details}
              </div>
            </div>
          )}

          {/* Timeline */}
          <div className="space-y-3">
            <Label className="text-xs text-muted-foreground">Historial</Label>
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm">
                <Clock className="w-4 h-4 text-muted-foreground" />
                <span className="text-muted-foreground">Creada:</span>
                <span className="font-medium">{timeAgo}</span>
                <span className="text-muted-foreground">({new Date(alarm.createdAt).toLocaleString('es-ES')})</span>
              </div>
              {alarm.acknowledgedAt && (
                <div className="flex items-center gap-2 text-sm">
                  <Check className="w-4 h-4 text-yellow-600" />
                  <span className="text-muted-foreground">Reconocida:</span>
                  <span className="font-medium">{new Date(alarm.acknowledgedAt).toLocaleString('es-ES')}</span>
                </div>
              )}
              {alarm.resolvedAt && (
                <div className="flex items-center gap-2 text-sm">
                  <CheckCircle2 className="w-4 h-4 text-green-600" />
                  <span className="text-muted-foreground">Resuelta:</span>
                  <span className="font-medium">{new Date(alarm.resolvedAt).toLocaleString('es-ES')}</span>
                </div>
              )}
              {alarm.actionTaken && (
                <div className="mt-2 p-3 bg-green-50 dark:bg-green-950/20 rounded-md">
                  <Label className="text-xs text-muted-foreground">Acción tomada</Label>
                  <p className="text-sm mt-1">{alarm.actionTaken}</p>
                </div>
              )}
            </div>
          </div>

          {/* Acción tomada (solo si está activa o reconocida) */}
          {(isActive || isAcknowledged) && (
            <div className="space-y-2">
              <Label htmlFor="actionTaken">Acción tomada (opcional)</Label>
              <Textarea
                id="actionTaken"
                placeholder="Describe la acción que se tomó para resolver esta alarma..."
                value={actionTaken}
                onChange={(e) => setActionTaken(e.target.value)}
                rows={3}
              />
            </div>
          )}
        </div>

        <DialogFooter>
          {isActive && (
            <>
              <Button
                variant="outline"
                onClick={() => onAcknowledge(alarm.id)}
                disabled={isAcknowledging || isResolving}
              >
                Reconocer
              </Button>
              <Button
                onClick={handleResolve}
                disabled={isAcknowledging || isResolving}
              >
                {isResolving ? 'Resolviendo...' : 'Resolver'}
              </Button>
            </>
          )}
          {isAcknowledged && (
            <Button
              onClick={handleResolve}
              disabled={isAcknowledging || isResolving}
            >
              {isResolving ? 'Resolviendo...' : 'Resolver'}
            </Button>
          )}
          {isResolved && (
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cerrar
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
